/**
 * Validation utilities for SDK responses
 */

export interface Resource {
  type?: string;
  url?: string;
  [key: string]: any;
}

/**
 * Validate that a resource contains a valid URL
 * @param resource The resource to validate
 * @param resourceType Type description for error messages (e.g., "image", "video")
 * @throws Error if the resource is invalid
 */
export function validateResourceUrl(resource: Resource, resourceType: string = 'resource'): void {
  // Check if URL exists
  if (!resource.url) {
    throw new Error(`SDK returned ${resourceType} without URL`);
  }

  // Check if it's a text response instead of URL
  if (resource.type === 'text') {
    throw new Error(`SDK returned text instead of ${resourceType}: ${resource.url}`);
  }

  // Check if URL is valid (starts with http)
  if (!resource.url.startsWith('http://') && !resource.url.startsWith('https://')) {
    throw new Error(`SDK returned invalid ${resourceType} URL: ${resource.url}`);
  }
}

/**
 * Validate an array of resources
 * @param resources The resources array to validate
 * @param resourceType Type description for error messages
 * @throws Error if any resource is invalid
 */
export function validateResources(resources: Resource[], resourceType: string = 'resource'): void {
  if (!resources || resources.length === 0) {
    throw new Error(`No ${resourceType}s returned from API`);
  }

  for (let i = 0; i < resources.length; i++) {
    try {
      validateResourceUrl(resources[i], resourceType);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`${resourceType} ${i + 1}/${resources.length}: ${errorMessage}`);
    }
  }
}
